import { formatCurrency } from "@sa-nu/shared-utils/price";
import clsx from "clsx";

import { useGtm } from "../../../../../lib/google-tag-manager/gtm";
import { useRoomDetailDrawer } from "../../../room-detail/room-detail-drawer-provider";
import { useOptionalPendingStaySelectionContext } from "../../../site-detail/_context/pending-stay-selection-context";
import { useRoomTypeReserveContext } from "../../_context/room-type-reserve-context";
import type { BrowseRoomTypeWithAvailability } from "../../_hooks/use-room-type-search";
import { isRoomTypeUnavailable } from "../../_utils/room-type-availability-view";
import { buildRoomDetailPayloadForReserve } from "../../reserve/build-room-detail-payload-for-reserve";
import { ReserveRoomTypePill } from "../../ui/reserve-room-type-pill";

import { ArchSpecRow } from "./_components/arch-spec-row";
import { FeatureSubtext } from "./_components/feature-subtext";
import { PriceSkeleton } from "./price-skeleton";

/**
 * Selection series かつ部屋タイプが1種類の建築タイプ向けカード（Figma 5182-12041）。
 *
 * なぜ専用コンポーネントか: Selection series は建築タイプ名が拠点名・部屋タイプ名としても
 * 繰り返し使われて冗長になる（実質 建築タイプ＝拠点＝部屋タイプ が 1 対 1）。そのため通常の
 * 展開グループ（ArchitectureTypeGroup）とは分け、繰り返しを畳んで 1 部屋アイテムを直接表示する
 * 特別扱いにしている。
 *
 * 展開グループ（ArchitectureTypeGroup）と同型の枠構成:
 *   - box border（dx-border-default）・px-[16px] py-[24px]・gap-[8px]
 *   - 建築タイプ名 22px
 *   - Props 行（人数/広さ・アイコン付き、ArchitectureTypeGroup と同仕様）
 *   - 1部屋アイテム: 価格 ＋ 予約ピルボタン
 *
 * 折りたたみ無し（最初から全部表示）。
 * 予約ボタン/画像タップで部屋タイプ詳細モーダルを開く。
 *
 * 予約フロー経路（`RoomTypeReserveContext` が提供されている場合）では
 * `reservation` を付けて開き、詳細モーダルに予約導線 CTA を表示する。
 */
export function SingleSelectionArchitecture({
  roomTypes,
  firstItem = false,
  showBorder = true,
}: {
  roomTypes: BrowseRoomTypeWithAvailability[];
  /** 先頭グループの SP border-t は隣接要素との区切りにならず浮くため外す */
  firstItem?: boolean;
  /** モーダル表示幅では lg:border がビューポート基準で誤発火するため呼び出し元から無効化する */
  showBorder?: boolean;
}) {
  const { open } = useRoomDetailDrawer();
  const gtm = useGtm();
  const reserveCtx = useRoomTypeReserveContext();
  // Provider の外（SiteCard 経由のブラウズ一覧）から呼ばれると null。
  // 拠点詳細ドロワー（SiteDetailBody）経由のときだけ non-null になる。
  const pendingStayCtx = useOptionalPendingStaySelectionContext();
  const roomType = roomTypes[0];
  const arch = roomType?.architectureType ?? null;
  const availabilities = roomType?.availabilities;
  const priceLoading = availabilities === undefined;
  const fromPrice = availabilities?.fromPrice ?? null;
  const unavailable = roomType != null && isRoomTypeUnavailable(roomType);

  const handleOpen = () => {
    if (!roomType) return;
    gtm.sendEvent("rt_rsv_card_reserve_clicked", { room_type_id: roomType.id });
    if (reserveCtx === null) {
      open({ roomTypeId: roomType.id });
      return;
    }
    // 満室の部屋タイプは日程フィルタを引き継がず、日付未選択のカレンダーで開いて
    // 空いている日を確認しやすくする。カード/画像タップは詳細を開くだけの操作のため
    // URL（ci/co）は書き換えず、選択中の一時日程は payload のスナップショットとして渡す
    // （URL反映は「予約に進む」ボタン押下に委ねる）。
    const ctxForOpen = unavailable
      ? { ...reserveCtx, firstNight: null, lastNight: null }
      : pendingStayCtx !== null
        ? {
            ...reserveCtx,
            firstNight: pendingStayCtx.firstNight,
            lastNight: pendingStayCtx.lastNight,
          }
        : reserveCtx;
    open(buildRoomDetailPayloadForReserve(roomType.id, ctxForOpen));
  };

  return (
    <div
      className={clsx(
        "border-dx-border-default flex flex-col gap-[8px] px-[16px] py-[16px]",
        showBorder && "lg:border",
        !firstItem && "border-t",
      )}
    >
      <p className="font-dx text-dx-text-default text-[22px] leading-[28px] font-medium">
        {arch?.name ?? "その他"}
      </p>

      <ArchSpecRow arch={arch} />

      {/* 1部屋アイテム（部屋タイプ名スロット無し・価格＋予約のみ）（Figma 5182-12041）。
          満室（空きなし）時は RoomTypeRow と揃えて予約ピルのみ淡色化する */}
      <button
        data-analytics-id="room_type_browse_opened_room_type"
        type="button"
        onClick={handleOpen}
        className={clsx(
          "flex w-full items-start gap-[16px] pt-[8px] text-left",
          "bg-dx-surface-base hover:bg-dx-bg-base-hover cursor-pointer transition-colors duration-150",
        )}
      >
        <div className="flex min-w-px flex-1 flex-col gap-[4px] pt-[4px]">
          {roomType && (
            <FeatureSubtext
              roomType={roomType}
              evChargerAvailable={reserveCtx?.evChargerAvailable ?? false}
            />
          )}
          {priceLoading ? (
            <PriceSkeleton />
          ) : unavailable ? (
            <p className="flex flex-wrap items-center gap-x-[4px]">
              <span className="font-notojp text-dx-text-sub text-[16px] leading-[28px] font-bold">
                ¥ ---
              </span>
              <span className="font-notojp text-dx-text-default text-[12px] leading-[18px]">
                /1泊
              </span>
            </p>
          ) : (
            fromPrice != null && (
              <p className="flex flex-wrap items-center gap-x-[4px]">
                <span className="font-notojp text-dx-text-sub text-[16px] leading-[28px] font-bold">
                  {formatCurrency(fromPrice)}〜
                </span>
                <span className="font-notojp text-dx-text-default text-[12px] leading-[18px]">
                  /1泊2名
                </span>
              </p>
            )
          )}
        </div>

        {/* 詳細・予約/部屋詳細ピル（RoomTypeRow と同じ ReserveRoomTypePill を使用） */}
        <div className="flex shrink-0 flex-col items-center justify-center">
          <ReserveRoomTypePill unavailable={unavailable} />
        </div>
      </button>
    </div>
  );
}
